import { useCallback, useEffect, useMemo } from "react";

import { Trophy } from "lucide-react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import { fetchPublishedLeaderboard } from "../../Store/Reducers/SponsorEngagementSlice";
import {
  buildLeaderboardQuarterOptions,
  getPublishedLeaderboardEmptyMessage,
} from "../../Utils/Helper/sponsorEngagementUi";
import LeaderboardSnapshotView, {
  type LeaderboardSnapshotPayload,
} from "../DiscoveryHub/LeaderboardSnapshotView";

type PublishedLeaderboardProps = {
  /** When true, renders inside Leaderboard page (no outer card shell). */
  embedded?: boolean;
};

const PublishedLeaderboard = ({
  embedded = false,
}: PublishedLeaderboardProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, loading } = useAppSelector(
    s => s.sponsorEngagement.publishedLeaderboard
  );
  const { orgSlug } = useParams<{ orgSlug?: string }>();
  const selectedPeriod = searchParams.get("period")?.trim() || undefined;

  const withOrg = useCallback(
    (path: string) => {
      const normalizedPath = path.startsWith("/") ? path : `/${path}`;
      return orgSlug ? `/${orgSlug}${normalizedPath}` : normalizedPath;
    },
    [orgSlug]
  );

  const handlePeriodChange = useCallback(
    (nextPeriod: string) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.set("period", nextPeriod);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  useEffect(() => {
    dispatch(fetchPublishedLeaderboard(selectedPeriod));
  }, [dispatch, selectedPeriod]);

  const snapshot = data as LeaderboardSnapshotPayload | null;
  const activePeriod = selectedPeriod || snapshot?.period;

  const periodOptions = useMemo(
    () =>
      buildLeaderboardQuarterOptions(
        activePeriod,
        snapshot?.period,
        snapshot?.current_period,
        snapshot?.available_periods
      ),
    [
      activePeriod,
      snapshot?.period,
      snapshot?.current_period,
      snapshot?.available_periods,
    ]
  );

  const emptyMessage = useMemo(
    () =>
      getPublishedLeaderboardEmptyMessage(
        activePeriod,
        snapshot?.publication_state,
        snapshot?.available_periods
      ),
    [activePeriod, snapshot?.publication_state, snapshot?.available_periods]
  );

  const goToDrilldown = useCallback(
    (
      sponsorId: string,
      drilldownPeriod?: string,
      drilldownCycleId?: string
    ) => {
      const returnTo = encodeURIComponent(
        `${location.pathname}${location.search}`
      );
      const q = new URLSearchParams({ sponsorId, returnTo });
      if (drilldownPeriod) q.set("period", drilldownPeriod);
      if (drilldownCycleId) q.set("cycleId", drilldownCycleId);
      navigate(`${withOrg("/sponsor-drilldown")}?${q.toString()}`);
    },
    [location.pathname, location.search, navigate, withOrg]
  );

  const content = (
    <div className="min-w-0">
      {!embedded ? (
        <div className="flex items-center justify-between mb-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2.5 mb-1">
              <span
                className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-gray-100 text-gray-600"
                aria-hidden
              >
                <Trophy className="h-5 w-5" />
              </span>
              <h1 className="text-2xl font-bold text-gray-900 mb-0">
                Sponsor Engagement Leaderboard
              </h1>
            </div>
            <p className="text-sm text-gray-600 font-normal mb-0 ml-9">
              Official published snapshot · 2-factor composite (Team Feedback,
              Engagement Activity)
            </p>
          </div>
        </div>
      ) : null}

      <LeaderboardSnapshotView
        data={snapshot}
        loading={loading}
        showDescription={!embedded}
        periodOptions={periodOptions}
        selectedPeriod={activePeriod}
        onPeriodChange={handlePeriodChange}
        emptyMessage={emptyMessage}
        onRowClick={row =>
          row.sponsor_id &&
          goToDrilldown(row.sponsor_id, activePeriod, snapshot?.cycle_id)
        }
      />
    </div>
  );

  if (embedded) {
    return <div className="min-w-0">{content}</div>;
  }

  return (
    <div className="w-full">
      <div className="border border-[#dee0e4] rounded-[15px] bg-white min-h-[calc(100vh-12.5rem)] p-6">
        {content}
      </div>
    </div>
  );
};

export default PublishedLeaderboard;
